/**
 * Passive Event Listeners Configuration
 * 
 * This utility adds passive event listener support to improve scroll performance
 * and eliminate browser warnings about non-passive event listeners.
 */

// Check if passive events are supported
let passiveSupported = false;
try {
  const options = {
    get passive() {
      passiveSupported = true;
      return false;
    }
  };
  window.addEventListener('test', null, options);
  window.removeEventListener('test', null, options);
} catch (err) {
  passiveSupported = false;
}

/**
 * Get event listener options with passive support
 * @param {boolean} usePassive - Whether to use passive mode
 * @returns {object|boolean} Options object or boolean
 */
export function getPassiveOptions(usePassive = true) {
  return passiveSupported ? { passive: usePassive } : false;
}

/**
 * Install passive event listener support globally
 * This patches addEventListener to automatically add passive option for scroll-blocking events
 */
export function installPassiveEventSupport() {
  if (!passiveSupported) return;

  // Events that should be passive by default for better performance
  const passiveEvents = ['wheel', 'touchstart', 'touchmove', 'scroll'];

  // Store original addEventListener
  const originalAddEventListener = EventTarget.prototype.addEventListener;

  // Patch addEventListener
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    // If this is a scroll-blocking event and no options specified
    if (passiveEvents.includes(type) && (options === undefined || options === false)) {
      // Make it passive by default
      options = { passive: true };
    } else if (typeof options === 'boolean') {
      // Convert boolean to object if needed
      options = { capture: options };
    } else if (typeof options === 'object' && options !== null) {
      // If options is an object but doesn't specify passive
      if (passiveEvents.includes(type) && options.passive === undefined) {
        options = { ...options, passive: true };
      }
    }

    // Call original addEventListener with new options
    return originalAddEventListener.call(this, type, listener, options);
  };
}

/**
 * Add passive event listener helper
 * @param {EventTarget} element - The element to attach the listener to
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {object} options - Additional options
 */
export function addPassiveEventListener(element, event, handler, options = {}) {
  const listenerOptions = passiveSupported 
    ? { ...options, passive: true }
    : (options.capture || false);
  
  element.addEventListener(event, handler, listenerOptions);
}

/**
 * Remove passive event listener helper
 * @param {EventTarget} element - The element to remove the listener from
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {object} options - Additional options
 */
export function removePassiveEventListener(element, event, handler, options = {}) {
  const listenerOptions = passiveSupported 
    ? { ...options, passive: true }
    : (options.capture || false);
  
  element.removeEventListener(event, handler, listenerOptions);
}

export default {
  getPassiveOptions,
  installPassiveEventSupport,
  addPassiveEventListener,
  removePassiveEventListener,
  passiveSupported
};

